/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchTableBookingBookings, fetchTableBookingLayouts } from "../../api/tableBookingApi";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import { showToast } from "../../store/features/toast/toastSlice";
import {
  buildLayoutSlotLookup,
  formatBookingCreated,
  formatBookingSlot,
  normalizeBookingsList,
  normalizeLayoutsList,
  pickBookingAmount,
  pickBookingCurrency,
  pickBookingDate,
  pickBookingGuestEmail,
  pickBookingGuestName,
  pickBookingGuestPhone,
  pickBookingPartySize,
  pickBookingRef,
  sortBookingsByCreatedDesc,
} from "./tableBookingUtils";
import { TableBookingCatalogSnapshot } from "../../components/clubCatalog/BookingCatalogSnapshots";
import "./tableBooking.css";

const TableBookingBookings = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [slotLookup, setSlotLookup] = useState({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const load = async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const [bookingsRes, layoutsRes] = await Promise.all([
        fetchTableBookingBookings(clubId, params),
        fetchTableBookingLayouts(clubId),
      ]);
      setRows(normalizeBookingsList(bookingsRes));
      setSlotLookup(buildLayoutSlotLookup(normalizeLayoutsList(layoutsRes)));
    } catch (err) {
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("TABLE_BOOKING_BOOKINGS_LOAD_ERROR"),
          type: "error",
        })
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clubId]);

  const onApplyRange = (e) => {
    e?.preventDefault?.();
    load();
  };

  const filtered = useMemo(() => {
    let r = rows;
    const st = statusFilter.trim().toLowerCase();
    if (st) {
      r = r.filter((b) =>
        String(b?.status ?? "")
          .toLowerCase()
          .includes(st)
      );
    }
    const df = dateFilter.trim();
    if (df) {
      r = r.filter((b) => String(pickBookingDate(b)).includes(df));
    }
    return sortBookingsByCreatedDesc(r);
  }, [rows, statusFilter, dateFilter]);

  return (
    <Card className="mainCard mainCardWrapper table-booking-bookings-card">
      <Card.Body className="table-booking-bookings-body">
        <form className="table-booking-bookings-toolbar mb-2" onSubmit={onApplyRange}>
          <div className="table-booking-bookings-toolbar-row">
            <div className="table-booking-bookings-field">
              <Form.Label className="table-booking-bookings-label">
                {t("TABLE_BOOKING_FROM_DATE")}
              </Form.Label>
              <Form.Control
                type="date"
                className="table-booking-bookings-input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="table-booking-bookings-field">
              <Form.Label className="table-booking-bookings-label">
                {t("TABLE_BOOKING_TO_DATE")}
              </Form.Label>
              <Form.Control
                type="date"
                className="table-booking-bookings-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="table-booking-bookings-field table-booking-bookings-field--action">
              <button
                type="submit"
                className="btn btn-primary table-booking-btn-load"
              >
                {t("TABLE_BOOKING_APPLY_RANGE")}
              </button>
            </div>
          </div>
          <div className="table-booking-bookings-toolbar-row table-booking-bookings-toolbar-row--filters">
            <div className="table-booking-bookings-field table-booking-bookings-field--grow">
              <Form.Label className="table-booking-bookings-label">
                {t("TABLE_BOOKING_FILTER_STATUS")}
              </Form.Label>
              <Form.Control
                type="search"
                className="table-booking-bookings-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                placeholder={t("TABLE_BOOKING_FILTER_STATUS_PH")}
              />
            </div>
            <div className="table-booking-bookings-field table-booking-bookings-field--grow">
              <Form.Label className="table-booking-bookings-label">
                {t("TABLE_BOOKING_FILTER_DATE")}
              </Form.Label>
              <Form.Control
                type="search"
                className="table-booking-bookings-input"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                placeholder={t("TABLE_BOOKING_FILTER_DATE_PH")}
              />
            </div>
          </div>
        </form>
        {loading ? (
          <CustomLoadingHTML />
        ) : filtered.length === 0 ? (
          <p className="text-muted mb-0">{t("TABLE_BOOKING_NO_BOOKINGS")}</p>
        ) : (
          <div className="table-responsive table-booking-bookings-scroll">
            <Table hover className="mb-0 align-middle small table-booking-bookings-table">
              <thead>
                <tr>
                  <th>{t("TABLE_BOOKING_COL_REF")}</th>
                  <th>{t("TABLE_BOOKING_COL_STATUS")}</th>
                  <th>{t("TABLE_BOOKING_COL_DATE")}</th>
                  <th>{t("TABLE_BOOKING_COL_SLOT")}</th>
                  <th>{t("TABLE_BOOKING_COL_PARTY")}</th>
                  <th>{t("TABLE_BOOKING_COL_AMOUNT")}</th>
                  <th>{t("TABLE_BOOKING_COL_USER")}</th>
                  <th>{t("TABLE_BOOKING_COL_CREATED")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => {
                  const ref = pickBookingRef(b);
                  const amt = pickBookingAmount(b);
                  const guestName = pickBookingGuestName(b);
                  const guestPhone = pickBookingGuestPhone(b);
                  const guestEmail = pickBookingGuestEmail(b);
                  const slotText = formatBookingSlot(b, slotLookup);
                  const tableText =
                    b?.table?.displayLabel ??
                    b?.areaLabel ??
                    b?.areaLabelDisplay ??
                    "";
                  return (
                    <tr key={ref + String(i)}>
                      <td>
                        <code>{ref}</code>
                      </td>
                      <td>{b?.status ?? "—"}</td>
                      <td>{pickBookingDate(b)}</td>
                      <td>
                        <div>{slotText}</div>
                        {tableText ? (
                          <div className="table-booking-guest-meta">
                            {tableText}
                          </div>
                        ) : null}
                      </td>
                      <td>{pickBookingPartySize(b)}</td>
                      <td>
                        {amt != null && amt !== ""
                          ? `${amt} ${pickBookingCurrency(b)}`.trim()
                          : "—"}
                      </td>
                      <td className="table-booking-guest-cell">
                        <div className="table-booking-guest-name">{guestName}</div>
                        {guestPhone ? (
                          <div className="table-booking-guest-meta">{guestPhone}</div>
                        ) : null}
                        {guestEmail ? (
                          <div className="table-booking-guest-meta">{guestEmail}</div>
                        ) : null}
                      </td>
                      <td>
                        <div>{formatBookingCreated(b)}</div>
                        <TableBookingCatalogSnapshot booking={b} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TableBookingBookings;
